import { useState } from "react";
import { Eye, Paperclip, ShieldAlert, ShieldCheck } from "lucide-react";
import { resolveFileUrl } from "../../utils/fileUrl";
import { NoticeChips } from "./NoticeParts";
import { NOTICE_PRIORITIES, formatNoticeDateTime, formatNoticeTime, isImage, needsAck, previewText } from "./noticeUtils";

/** First image of the notice as a square thumbnail (hidden if the signed link no longer loads). */
function Thumbnail({ image, count, compact }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <span className={`relative shrink-0 overflow-hidden rounded-lg bg-app-hover ${compact ? "h-16 w-16" : "h-20 w-20 sm:h-24 sm:w-24"}`}>
      <img src={resolveFileUrl(image.url)} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
      {count > 1 && (
        <span className="absolute bottom-1 right-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">+{count - 1}</span>
      )}
    </span>
  );
}

/** A notice in a list: unread dot, chips, title, preview and acknowledgement state. */
function NoticeListItem({ notice, onOpen, showStats = false, compact = false }) {
  const unread = !notice.readAt && notice.status === "active";
  const ackPending = needsAck(notice);
  const accent = (NOTICE_PRIORITIES[notice.priority] || NOTICE_PRIORITIES.normal).accent;
  const when = notice.publishAt;
  const images = (notice.attachments || []).filter((a) => isImage(a.mimeType));

  return (
    <button
      type="button"
      data-notice-item
      onClick={() => onOpen(notice.id)}
      className={`group relative flex w-full gap-3 rounded-xl border border-app-primary border-l-4 ${accent} p-3.5 text-left transition-all hover:-translate-y-px hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
        unread ? "bg-blue-50/50 dark:bg-blue-500/[0.07]" : "bg-app-card"
      }`}
    >
      <span
        aria-hidden="true"
        className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${unread ? "bg-blue-600 dark:bg-sky-400" : "bg-transparent"}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <NoticeChips notice={notice} showStatus={showStats} />
          <span className="text-xs text-app-muted" title={formatNoticeDateTime(when)}>
            {notice.status === "scheduled" ? `Goes live ${formatNoticeDateTime(when)}` : formatNoticeTime(when)}
          </span>
        </div>
        <p className={`mt-1.5 text-[15px] leading-snug text-app-heading ${unread ? "font-semibold" : "font-medium"} ${compact ? "line-clamp-1" : "line-clamp-2"}`}>
          {notice.title}
          {unread && <span className="sr-only"> (unread)</span>}
        </p>
        <p className={`mt-1 text-sm text-app-muted ${compact ? "line-clamp-1" : "line-clamp-2"}`}>{previewText(notice.body, compact ? 140 : 260)}</p>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-app-muted">
          {notice.author && <span>{notice.author.name}</span>}
          {notice.attachments?.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Paperclip className="h-3.5 w-3.5" aria-hidden="true" /> {notice.attachments.length}
            </span>
          )}
          {ackPending && (
            <span className="inline-flex items-center gap-1 font-semibold text-amber-700 dark:text-amber-300">
              <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" /> Acknowledgement needed
            </span>
          )}
          {notice.requiresAck && notice.acknowledgedAt && (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Acknowledged
            </span>
          )}
          {showStats && notice.stats && (
            <span className="ml-auto inline-flex items-center gap-3 tabular-nums">
              <span className="inline-flex items-center gap-1" title="Read">
                <Eye className="h-3.5 w-3.5" aria-hidden="true" /> {notice.stats.readCount}/{notice.stats.audience}
              </span>
              {notice.requiresAck && (
                <span className="inline-flex items-center gap-1" title="Acknowledged">
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> {notice.stats.ackCount}/{notice.stats.audience}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      {images.length > 0 && <Thumbnail key={images[0].id} image={images[0]} count={images.length} compact={compact} />}
    </button>
  );
}

export default NoticeListItem;
